/**
 * Централизованная карта React Query keys.
 *
 * Все queryKey приложения — здесь. Это позволяет:
 *   • точечно инвалидировать после мутации:
 *     `qc.invalidateQueries({queryKey: keys.auth.permissions()})`
 *   • инвалидировать всё домена:
 *     `qc.invalidateQueries({queryKey: keys.event.all})`
 *   • поймать в одном месте если ключи разъезжаются
 */

import type {GetCashierReportRequest} from '@services/cashier';
import type {EventSalesDailyRequest, GetEventsRequest} from '@services/event';
import type {GetOrdersRequest} from '@services/order';
import type {GetRefundsRequest} from '@services/refund';
import type {GetAnnualReportRequest, GetReportsRequest} from '@services/report';
import type {
  GetEventStatisticsRequest,
  GetPriceStatisticsRequest,
  GetSaleStatisticsRequest,
} from '@services/statistics';
import type {GetTaxesRequest} from '@services/tax';

export const keys = {
  auth: {
    all: ['auth'] as const,
    permissions: () => [...keys.auth.all, 'permissions'] as const,
    /** Итоговые permission-ключи выбранного контекста. */
    permissionKeys: (permissionId: number | null) =>
      [...keys.auth.all, 'permission-keys', permissionId ?? 'none'] as const,
  },
  event: {
    all: ['event'] as const,
    list: (req: GetEventsRequest) => [...keys.event.all, 'list', req] as const,
    detail: (id: number | string) => [...keys.event.all, 'detail', String(id)] as const,
    sessions: (id: number | string) => [...keys.event.all, 'sessions', String(id)] as const,
    salesDaily: (req: EventSalesDailyRequest) => [...keys.event.all, 'sales-daily', req] as const,
  },
  order: {
    all: ['order'] as const,
    list: (req: GetOrdersRequest) => [...keys.order.all, 'list', req] as const,
    /** Раздел «Брони» — отдельный эндпоинт, свой ключ. */
    booked: () => [...keys.order.all, 'booked'] as const,
    detail: (id: number | string) => [...keys.order.all, 'detail', String(id)] as const,
    ticketsHtml: (id: number | string, ticketType: string) =>
      [...keys.order.all, 'tickets-html', String(id), ticketType] as const,
    ticketHtml: (id: number | string, ticketType: string) =>
      [...keys.order.all, 'ticket-html', String(id), ticketType] as const,
  },
  basket: {
    all: ['basket'] as const,
    /** Активная корзина — keyed by sessionId, чтобы при смене сессии
     *  query автоматически переключался. */
    active: (sessionId: number) => [...keys.basket.all, 'active', sessionId] as const,
  },
  payment: {
    all: ['payment'] as const,
    methods: () => [...keys.payment.all, 'methods'] as const,
  },
  actionLog: {
    all: ['action-log'] as const,
    byOrder: (id: number | string) => [...keys.actionLog.all, 'order', String(id)] as const,
  },
  theater: {
    all: ['theater'] as const,
    list: () => [...keys.theater.all, 'list'] as const,
    detail: (id: number | string) => [...keys.theater.all, 'detail', String(id)] as const,
  },
  theaterType: {
    all: ['theater-type'] as const,
    list: () => [...keys.theaterType.all, 'list'] as const,
  },
  region: {
    all: ['region'] as const,
    list: () => [...keys.region.all, 'list'] as const,
  },
  country: {
    all: ['country'] as const,
    list: () => [...keys.country.all, 'list'] as const,
  },
  customer: {
    all: ['customer'] as const,
    list: (req: unknown) => [...keys.customer.all, 'list', req] as const,
    detail: (id: number | string) => [...keys.customer.all, 'detail', String(id)] as const,
    orders: (id: number | string) => [...keys.customer.all, 'orders', String(id)] as const,
    tickets: (id: number | string) => [...keys.customer.all, 'tickets', String(id)] as const,
    ips: (id: number | string) => [...keys.customer.all, 'ips', String(id)] as const,
  },
  scheme: {
    all: ['scheme'] as const,
    byHall: (hallId: number, inactive = false) =>
      [...keys.scheme.all, 'byHall', hallId, inactive ? 'inactive' : 'active'] as const,
    detail: (id: number | string) => [...keys.scheme.all, 'detail', String(id)] as const,
    svg: (path: string) => [...keys.scheme.all, 'svg', path] as const,
  },
  dss: {
    all: ['dss'] as const,
    shows: () => [...keys.dss.all, 'shows'] as const,
    scheme: (sessionId: number | string) => [...keys.dss.all, 'scheme', String(sessionId)] as const,
    sectionSeats: (sessionId: number | string, sectionId: number | string) =>
      [...keys.dss.all, 'section-seats', String(sessionId), String(sectionId)] as const,
  },
  esbo: {
    all: ['esbo'] as const,
    seats: (outerSessionId: number | string) =>
      [...keys.esbo.all, 'seats', String(outerSessionId)] as const,
    pricing: (outerSessionId: number | string) =>
      [...keys.esbo.all, 'pricing', String(outerSessionId)] as const,
  },
  session: {
    all: ['session'] as const,
    detail: (id: number | string) => [...keys.session.all, 'detail', String(id)] as const,
    /** Публичная шапка сеанса по слагу — название, дата, площадка. */
    bySlug: (slug: string) => [...keys.session.all, 'by-slug', slug] as const,
    templates: (schemeId: number) => [...keys.session.all, 'templates', schemeId] as const,
    analytics: (id: number | string) => [...keys.session.all, 'analytics', String(id)] as const,
    salesChannels: (id: number | string) =>
      [...keys.session.all, 'sales-channels', String(id)] as const,
    scanned: (id: number | string) => [...keys.session.all, 'scanned', String(id)] as const,
    salesByPrice: (id: number | string) =>
      [...keys.session.all, 'sales-by-price', String(id)] as const,
    priceDetails: (eventId: number | string, sessionId: number | string) =>
      [...keys.session.all, 'price-details', String(eventId), String(sessionId)] as const,
    priceHistory: (id: number | string, color: string) =>
      [...keys.session.all, 'price-history', String(id), color] as const,
    ticketsInfo: (id: number | string) =>
      [...keys.session.all, 'tickets-info', String(id)] as const,
    schemeInfo: (id: number | string) => [...keys.session.all, 'scheme-info', String(id)] as const,
  },
  /** Tabs Checker: видимость блоков в карточке сеанса на сайте. */
  sessionBlocks: {
    all: ['session-blocks'] as const,
    /** Справочник блоков — `GET blocks/schema`. */
    schema: () => [...keys.sessionBlocks.all, 'schema'] as const,
    /** Сеансы, у которых что-то выключено. */
    list: () => [...keys.sessionBlocks.all, 'list'] as const,
    detail: (slug: string) => [...keys.sessionBlocks.all, 'detail', slug] as const,
  },
  color: {
    all: ['color'] as const,
    list: () => [...keys.color.all, 'list'] as const,
  },
  category: {
    all: ['category'] as const,
    list: () => [...keys.category.all, 'list'] as const,
  },
  organization: {
    all: ['organization'] as const,
    list: (req?: unknown) =>
      req === undefined
        ? ([...keys.organization.all, 'list'] as const)
        : ([...keys.organization.all, 'list', req] as const),
    /** Полный справочник (все страницы) — для Select'ов. */
    allList: () => [...keys.organization.all, 'list', 'all'] as const,
  },
  user: {
    all: ['user'] as const,
    list: (search?: string) => [...keys.user.all, 'list', search ?? ''] as const,
    detail: (id: number | string) => [...keys.user.all, 'detail', String(id)] as const,
    permissions: (id: number | string) => [...keys.user.all, 'permissions', String(id)] as const,
    cashiers: () => [...keys.user.all, 'cashiers'] as const,
  },
  role: {
    all: ['role'] as const,
    list: () => [...keys.role.all, 'list'] as const,
    detail: (role: string) => [...keys.role.all, 'detail', role] as const,
  },
  refund: {
    all: ['refund'] as const,
    list: (req: GetRefundsRequest) => [...keys.refund.all, 'list', req] as const,
    detail: (id: number | string) => [...keys.refund.all, 'detail', String(id)] as const,
  },
  report: {
    all: ['report'] as const,
    list: (req: GetReportsRequest) => [...keys.report.all, 'list', req] as const,
    annual: (req: GetAnnualReportRequest) => [...keys.report.all, 'annual', req] as const,
  },
  discount: {
    all: ['discount'] as const,
    list: () => [...keys.discount.all, 'list'] as const,
    meta: () => [...keys.discount.all, 'meta'] as const,
  },
  cashier: {
    all: ['cashier'] as const,
    report: (req: GetCashierReportRequest) => [...keys.cashier.all, 'report', req] as const,
  },
  sms: {
    all: ['sms'] as const,
    codes: () => [...keys.sms.all, 'codes'] as const,
    platforms: () => [...keys.sms.all, 'platforms'] as const,
  },
  partner: {
    all: ['partner'] as const,
    list: () => [...keys.partner.all, 'list'] as const,
  },
  pointOfSale: {
    all: ['point-of-sale'] as const,
    list: () => [...keys.pointOfSale.all, 'list'] as const,
  },
  paymentMethod: {
    all: ['payment-method'] as const,
  },
  banner: {
    all: ['banner'] as const,
    list: () => [...keys.banner.all, 'list'] as const,
  },
  notification: {
    all: ['notification'] as const,
    list: () => [...keys.notification.all, 'list'] as const,
    categories: () => [...keys.notification.all, 'categories'] as const,
  },
  partnerChannel: {
    all: ['partner-channel'] as const,
    myPartners: () => [...keys.partnerChannel.all, 'my-partners'] as const,
    eventsPartners: () => [...keys.partnerChannel.all, 'events-partners'] as const,
  },
  statistics: {
    all: ['statistics'] as const,
    byEvents: (req: GetEventStatisticsRequest) =>
      [...keys.statistics.all, 'by-events', req] as const,
    byPrice: (req: GetPriceStatisticsRequest) => [...keys.statistics.all, 'by-price', req] as const,
    byDate: (req: GetSaleStatisticsRequest) => [...keys.statistics.all, 'by-date', req] as const,
  },
  duplicate: {
    all: ['duplicate'] as const,
    byEvent: (eventId: number | string | null) =>
      [...keys.duplicate.all, 'by-event', eventId == null ? null : String(eventId)] as const,
  },
  tax: {
    all: ['tax'] as const,
    list: (req: GetTaxesRequest) => [...keys.tax.all, 'list', req] as const,
  },
} as const;
